import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Search, Pencil, Trash2, Loader2, Contact, Building2, User, Users } from 'lucide-react';
import PortalLayout from './PortalLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const portalApi = axios.create({ baseURL: API_URL });

const EMPTY = {
  name: '', buyer_type: 'individual', tin: '', id_type: 'NRIC', id_value: '',
  sst_no: '', email: '', phone: '', address: '', city: '', state: '', postal_code: '', country: 'MYS',
};

export default function PortalCustomers() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const token = localStorage.getItem('einvoice_token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!token) { navigate('/einvoice/portal/auth'); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async (q = '') => {
    setLoading(true);
    try {
      const { data } = await portalApi.get(`/api/einvoice/portal/customers${q ? `?search=${encodeURIComponent(q)}` : ''}`, { headers });
      setCustomers(data.customers || []);
    } catch (e) {
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => { setEditingId(null); setForm(EMPTY); setShowForm(true); };
  const openEdit = (c) => { setEditingId(c.id); setForm({ ...EMPTY, ...c }); setShowForm(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Customer name is required'); return; }
    setSaving(true);
    try {
      if (editingId) {
        await portalApi.put(`/api/einvoice/portal/customers/${editingId}`, form, { headers });
        toast.success('Customer updated');
      } else {
        await portalApi.post('/api/einvoice/portal/customers', form, { headers });
        toast.success('Customer saved');
      }
      setShowForm(false);
      load(search);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save customer');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await portalApi.delete(`/api/einvoice/portal/customers/${deleteId}`, { headers });
      toast.success('Customer deleted');
      setDeleteId(null);
      load(search);
    } catch (e) {
      toast.error('Failed to delete customer');
    }
  };

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <PortalLayout>
      <div className="space-y-5" data-testid="customers-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
              <Contact className="w-5 h-5 text-blue-600 dark:text-blue-400" /> Customers
            </h1>
            <p className="text-sm text-slate-500 dark:text-gray-400">Save customers &amp; their TINs once, then auto-fill them on invoices.</p>
          </div>
          <Button onClick={openAdd} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="add-customer-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Customer
          </Button>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') load(search); }}
            placeholder="Search by name, TIN, ID or email…"
            className="pl-9 h-9"
            data-testid="customer-search"
          />
        </div>

        {/* List */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="py-16 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
            ) : customers.length === 0 ? (
              <div className="py-16 text-center" data-testid="customers-empty">
                <Contact className="w-10 h-10 mx-auto text-slate-300 dark:text-zinc-700 mb-3" />
                <p className="text-sm font-medium text-slate-600 dark:text-gray-300">No customers yet</p>
                <p className="text-xs text-slate-400 dark:text-gray-500 mt-1">Add a customer to reuse their tax details on invoices.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="customers-table">
                  <thead>
                    <tr className="text-left text-slate-500 dark:text-gray-400 border-b border-slate-200 dark:border-zinc-800">
                      <th className="py-2.5 px-4 font-medium">Name</th>
                      <th className="py-2.5 px-4 font-medium">Type</th>
                      <th className="py-2.5 px-4 font-medium">TIN</th>
                      <th className="py-2.5 px-4 font-medium">ID</th>
                      <th className="py-2.5 px-4 font-medium">Email</th>
                      <th className="py-2.5 px-4 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customers.map(c => (
                      <tr key={c.id} className="border-b border-slate-100 dark:border-zinc-800/70 hover:bg-slate-50 dark:hover:bg-zinc-800/50" data-testid={`customer-row-${c.id}`}>
                        <td className="py-2.5 px-4 font-medium text-slate-800 dark:text-white">{c.name}</td>
                        <td className="py-2.5 px-4">
                          <Badge variant="outline" className="text-[10px] capitalize gap-1">
                            {c.buyer_type === 'company' ? <Building2 className="w-3 h-3" /> : <User className="w-3 h-3" />}{c.buyer_type}
                          </Badge>
                        </td>
                        <td className="py-2.5 px-4 text-slate-600 dark:text-gray-300 font-mono text-xs">{c.tin || '—'}</td>
                        <td className="py-2.5 px-4 text-slate-600 dark:text-gray-300 text-xs">{c.id_value ? `${c.id_type}: ${c.id_value}` : '—'}</td>
                        <td className="py-2.5 px-4 text-slate-600 dark:text-gray-300 text-xs">{c.email || '—'}</td>
                        <td className="py-2.5 px-4 text-right whitespace-nowrap">
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => openEdit(c)} data-testid={`edit-customer-${c.id}`}>
                            <Pencil className="w-4 h-4 text-slate-500" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setDeleteId(c.id)} data-testid={`delete-customer-${c.id}`}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Add/Edit dialog */}
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="customer-form-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {editingId ? <Pencil className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
                {editingId ? 'Edit Customer' : 'Add Customer'}
              </DialogTitle>
              <DialogDescription>Details saved here can be auto-filled when creating invoices.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Buyer Type</Label>
                  <Select value={form.buyer_type} onValueChange={v => upd('buyer_type', v)}>
                    <SelectTrigger className="h-9" data-testid="cust-buyer-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="individual">Individual</SelectItem>
                      <SelectItem value="company">Company</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">ID Type</Label>
                  <Select value={form.id_type} onValueChange={v => upd('id_type', v)}>
                    <SelectTrigger className="h-9" data-testid="cust-id-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="NRIC">NRIC</SelectItem>
                      <SelectItem value="PASSPORT">Passport</SelectItem>
                      <SelectItem value="BRN">BRN</SelectItem>
                      <SelectItem value="ARMY">Army</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs">Name *</Label>
                <Input value={form.name} onChange={e => upd('name', e.target.value)} placeholder="Full name or company name" className="h-9" data-testid="cust-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">TIN</Label>
                  <Input value={form.tin} onChange={e => upd('tin', e.target.value)} placeholder="e.g. C1234567890" className="h-9" data-testid="cust-tin" />
                </div>
                <div>
                  <Label className="text-xs">ID Value</Label>
                  <Input value={form.id_value} onChange={e => upd('id_value', e.target.value)} placeholder="NRIC / Passport / BRN" className="h-9" data-testid="cust-id-value" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">SST No.</Label>
                  <Input value={form.sst_no} onChange={e => upd('sst_no', e.target.value)} placeholder="Optional" className="h-9" />
                </div>
                <div>
                  <Label className="text-xs">Email</Label>
                  <Input value={form.email} onChange={e => upd('email', e.target.value)} placeholder="name@example.com" className="h-9" data-testid="cust-email" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Phone</Label>
                  <Input value={form.phone} onChange={e => upd('phone', e.target.value)} placeholder="Optional" className="h-9" />
                </div>
                <div>
                  <Label className="text-xs">Postal Code</Label>
                  <Input value={form.postal_code} onChange={e => upd('postal_code', e.target.value)} placeholder="Optional" className="h-9" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Address</Label>
                <Input value={form.address} onChange={e => upd('address', e.target.value)} placeholder="Street address" className="h-9" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">City</Label>
                  <Input value={form.city} onChange={e => upd('city', e.target.value)} placeholder="Optional" className="h-9" />
                </div>
                <div>
                  <Label className="text-xs">State</Label>
                  <Input value={form.state} onChange={e => upd('state', e.target.value)} placeholder="Optional" className="h-9" />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-customer-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Users className="w-4 h-4 mr-2" />}
                {editingId ? 'Save Changes' : 'Save Customer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete confirm */}
        <Dialog open={!!deleteId} onOpenChange={o => { if (!o) setDeleteId(null); }}>
          <DialogContent className="max-w-sm" data-testid="customer-delete-modal">
            <DialogHeader>
              <DialogTitle>Delete customer?</DialogTitle>
              <DialogDescription>This will permanently remove the saved customer. Existing invoices are not affected.</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteId(null)}>Cancel</Button>
              <Button onClick={confirmDelete} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-customer-btn">
                <Trash2 className="w-4 h-4 mr-2" /> Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </PortalLayout>
  );
}
